import { useState } from "react";
import { useNavigate } from "react-router-dom";
import image from "../Assert/Images/logo/logo192.png";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [eFocus, setEFocus] = useState(false);
  const [pFocus, setPFocus] = useState(false);
  const [hover, setHover] = useState(false);
  const navigate = useNavigate();

  // Enhanced fetch with better CORS handling
  const enhancedFetch = async (url, options = {}) => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 15000);

    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
        mode: 'cors',
        credentials: 'include', // Important for cookies/sessions
      });

      clearTimeout(timeoutId);
      return response;
    } catch (error) {
      clearTimeout(timeoutId);
      throw error;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    // Basic validation
    if (!email || !password) {
      setError("Please fill in all fields");
      setLoading(false);
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError("Please enter a valid email address");
      setLoading(false);
      return;
    }

    try {
      const API_BASE_URL = "http://192.250.226.161:3002";
      
      const res = await enhancedFetch(`${API_BASE_URL}/api/admin/login`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          "Accept": "application/json",
        },
        credentials: "include", // This is crucial for CORS with credentials
        body: JSON.stringify({ email, password }),
      });

      // Handle non-JSON responses
      const contentType = res.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await res.text();
        console.error('Non-JSON response:', text);
        throw new Error('Server returned non-JSON response');
      }

      const data = await res.json();
      
      if (data.success) {
        // Store authentication token if provided
        if (data.token) {
          localStorage.setItem("adminToken", data.token);
          // Also set in sessionStorage for redundancy
          sessionStorage.setItem("adminToken", data.token);
        }
        if (data.data?.token) {
          localStorage.setItem("adminToken", data.data.token);
          sessionStorage.setItem("adminToken", data.data.token);
        }
        
        // Store user data if provided
        if (data.data?.user) {
          localStorage.setItem("adminUser", JSON.stringify(data.data.user));
        }
        
        navigate("/admin/admindashboard");
      } else {
        setError(data.message || "Login failed. Please check your credentials.");
      }
    } catch (err) {
      console.error("Login error:", err);
      
      // Enhanced error handling for CORS
      if (err.name === 'AbortError') {
        setError("Request timeout. Please check your connection and try again.");
      } else if (err.name === 'TypeError') {
        if (err.message.includes('Failed to fetch')) {
          setError("Network error. Please check:\n• Internet connection\n• Backend server status\n• CORS configuration");
        } else if (err.message.includes('CORS')) {
          setError("Cross-origin request blocked. Please contact support.");
        }
      } else if (err.message.includes('non-JSON')) {
        setError("Server error. Please try again later.");
      } else {
        setError(err.message || "An unexpected error occurred. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Alternative: Using environment variables
  const handleSubmitWithEnv = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const API_URL = process.env.REACT_APP_API_URL || "http://192.250.226.161:3333";
      
      const res = await fetch(`${API_URL}/api/admin/login`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({ email, password }),
      });

      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`);
      }

      const data = await res.json();
      
      if (data.success) {
        if (data.token) {
          localStorage.setItem("adminToken", data.token);
        }
        navigate("/admin/admindashboard");
      } else {
        setError(data.message || "Login failed.");
      }
    } catch (err) {
      setError("Connection failed. Please try again.");
      console.error("Login error:", err);
    } finally {
      setLoading(false);
    }
  };

  // Solution 3: Proxy configuration for development
  const handleSubmitWithProxy = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      // This will proxy through your React dev server
      const res = await fetch("/api/admin/login", {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await res.json();
      
      if (data.success) {
        if (data.token) {
          localStorage.setItem("adminToken", data.token);
        }
        navigate("/admin/admindashboard");
      } else {
        setError(data.message || "Login failed.");
      }
    } catch (err) {
      setError("Connection failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Styles remain the same as your original code
  const s = {
    page: {
      minHeight: "100vh",
      display: "grid",
      placeItems: "center",
      background: "linear-gradient(135deg, #f5f7fb 0%, #e4edf5 100%)",
      padding: 16,
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    },
    card: {
      width: "100%",
      maxWidth: 480,
      background: "#fff",
      borderRadius: 20,
      border: "1px solid #eef2f7",
      boxShadow: "0 24px 56px rgba(15,23,42,.12)",
      overflow: "hidden",
      transition: "transform 0.3s ease, box-shadow 0.3s ease",
    },
    body: {
      padding: "32px 32px 24px",
    },
    header: {
      textAlign: "center",
      marginBottom: 24,
    },
    logo: {
      width: 80,
      height: 80,
      display: "block",
      margin: "0 auto 16px",
      objectFit: "contain",
      borderRadius: 12,
    },
    title: {
      fontSize: 32,
      fontWeight: 800,
      color: "#0f172a",
      letterSpacing: "-0.5px",
      margin: "0 0 8px 0",
      background: "linear-gradient(135deg, #0f172a 0%, #334155 100%)",
      WebkitBackgroundClip: "text",
      WebkitTextFillColor: "transparent",
    },
    sub: {
      color: "#64748b",
      fontSize: 16,
      fontWeight: 500,
      margin: 0,
    },
    form: {
      display: "grid",
      gap: 24,
    },
    label: {
      fontWeight: 600,
      color: "#334155",
      marginBottom: 8,
      display: "block",
      fontSize: 14,
    },
    input: (focused) => ({
      width: "100%",
      background: focused ? "#fff" : "#f8fafc",
      color: "#0f172a",
      border: `1px solid ${focused ? "#E36324" : "#e2e8f0"}`,
      borderRadius: 12,
      boxSizing: "border-box",
      padding: "14px 16px",
      outline: "none",
      fontSize: 15,
      fontWeight: 500,
      transition: "all 0.2s ease",
      boxShadow: focused ? "0 0 0 3px rgba(227, 99, 36, 0.15)" : "none",
      "::placeholder": {
        color: "#94a3b8",
        fontWeight: 400,
      },
    }),
    error: {
      background: "#fef2f2",
      border: "1px solid #fecaca",
      color: "#dc2626",
      borderRadius: 10,
      padding: "12px 16px",
      fontSize: 14,
      fontWeight: 500,
      display: "flex",
      alignItems: "center",
      gap: 8,
      whiteSpace: 'pre-line',
    },
    button: (isHover, isLoading) => ({
      width: "100%",
      border: "none",
      borderRadius: 12,
      padding: "16px",
      background: "linear-gradient(135deg, #E36324 0%, #FF7B42 100%)",
      color: "#fff",
      fontWeight: 700,
      fontSize: 15,
      letterSpacing: "0.2px",
      cursor: isLoading ? "not-allowed" : "pointer",
      transition: "all 0.2s ease",
      boxShadow: "0 8px 24px rgba(227, 99, 36, 0.25)",
      opacity: isLoading ? 0.7 : 1,
      position: "relative",
      overflow: "hidden",
      ...(isHover && !isLoading && {
        transform: "translateY(-2px)",
        boxShadow: "0 12px 28px rgba(227, 99, 36, 0.35)",
      }),
    }),
    buttonContent: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 8,
    },
    spinner: {
      width: 16,
      height: 16,
      border: "2px solid transparent",
      borderTop: "2px solid #fff",
      borderRadius: "50%",
      animation: "spin 1s linear infinite",
    },
    footer: {
      textAlign: "center",
      padding: "20px 32px",
      color: "#94a3b8",
      fontSize: 13,
      fontWeight: 500,
      borderTop: "1px solid #f1f5f9",
      background: "#fafbfc",
    },
    styleTag: `
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `,
  };

  return (
    <div style={s.page}>
      <style>{s.styleTag}</style>
      
      <div style={s.card}>
        <div style={s.body}>
          <div style={s.header}>
            <img
              src={image}
              alt="Mitha Homes"
              style={s.logo}
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
            <h1 style={s.title}>Admin Login</h1>
            <div style={s.sub}>Access your management dashboard</div>
          </div>

          <form onSubmit={handleSubmit} style={s.form}>
            <div>
              <label style={s.label}>Email Address</label>
              <input
                type="email"
                placeholder="mithahomes@gmail.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onFocus={() => setEFocus(true)}
                onBlur={() => setEFocus(false)}
                autoComplete="email"
                autoFocus
                disabled={loading}
                style={s.input(eFocus)}
              />
            </div>

            <div>
              <label style={s.label}>Password</label>
              <input
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onFocus={() => setPFocus(true)}
                onBlur={() => setPFocus(false)}
                autoComplete="current-password"
                disabled={loading}
                style={s.input(pFocus)}
              />
            </div>

            {error && (
              <div style={s.error}>
                {error}
              </div>
            )}

            <button
              type="submit"
              style={s.button(hover, loading)}
              onMouseEnter={() => setHover(true)}
              onMouseLeave={() => setHover(false)}
              disabled={loading}
            >
              <div style={s.buttonContent}>
                {loading && <div style={s.spinner}></div>}
                {loading ? "Signing In..." : "Sign In"}
              </div>
            </button>
          </form>
        </div>

        <div style={s.footer}>
          © {new Date().getFullYear()} Mitha Homes. All rights reserved.
        </div>
      </div>
    </div>
  );
}